import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  Users,
  Bell,
  Settings,
  CreditCard,
  HelpCircle,
  ShieldAlert,
  Send,
  UserPlus,
  ArrowUpRight,
  BarChart3,
  Menu,
  ChevronDown,
  LogOut,
  Search,
  X,
  Sparkles,
  Receipt,
  Plug,
  Lightbulb,
} from 'lucide-react';
import { Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Avatar } from '@/components/ui/avatar';
import { BRAND } from '@maintenance-tracker/shared';
import { BrandMark } from '@/components/brand/brand-logo';
import { DevPlanSwitcher } from '@/legacy/components/dev-plan-switcher';
import { AppShellBanner } from '@/legacy/components/layout/app-shell-banner';
import { FeedbackPrompt } from '@/components/feedback-prompt';
import { useSupport } from '@/lib/support-context';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/retention', label: 'At risk', icon: ShieldAlert },
  { to: '/campaigns', label: 'Campaigns', icon: Send },
  { to: '/opportunities', label: 'Memberships', icon: UserPlus },
  { to: '/upgrades', label: 'Equipment', icon: ArrowUpRight },
  { to: '/insights', label: 'Technicians', icon: BarChart3 },
  { to: '/agreements', label: 'Agreements', icon: FileText },
  { to: '/customers', label: 'Customers', icon: Users },
  { to: '/reminders', label: 'Reminder log', icon: Bell },
];

const settingsItems = [
  { to: '/settings', label: 'Settings', icon: Settings },
  { to: '/settings/integrations', label: 'Integrations', icon: Plug },
  { to: '/settings/payments', label: 'Renewal links', icon: CreditCard },
  { to: '/settings/billing', label: 'Billing', icon: Receipt },
  { to: '/help', label: 'Help', icon: HelpCircle },
];

function NavLink({
  to,
  label,
  icon: Icon,
  active,
  onClick,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        'group relative flex items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors',
        active
          ? 'bg-sidebar-active text-sidebar-text-active'
          : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
      )}
      style={{ height: '38px' }}
    >
      {active && (
        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-white" />
      )}
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {label}
    </Link>
  );
}

function UserMenuDropdown({
  variant,
  onNavigate,
}: {
  variant: 'sidebar' | 'header';
  onNavigate?: () => void;
}) {
  const { user, usage, logout } = useAuth();
  const { openSupport } = useSupport();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const plan =
    usage?.trialDaysLeft != null && usage.trialDaysLeft > 0
      ? `Trial · ${usage.trialDaysLeft} ${usage.trialDaysLeft === 1 ? 'day' : 'days'} left`
      : usage?.tierName
        ? `${usage.tierName} plan`
        : 'Free plan';

  const close = () => setOpen(false);

  const menu = open && (
    <div
      className={cn(
        'absolute z-50 overflow-hidden rounded-xl border border-border bg-bg-surface py-1 shadow-dropdown',
        variant === 'sidebar'
          ? 'bottom-full left-3 right-3 mb-2 animate-slide-up'
          : 'right-0 top-full mt-2 w-52 animate-fade-in',
      )}
    >
      <Link
        to="/settings"
        className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-bg-subtle"
        onClick={() => {
          close();
          onNavigate?.();
        }}
      >
        <Settings className="h-4 w-4" /> Account settings
      </Link>
      <Link
        to="/settings/billing"
        className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-bg-subtle"
        onClick={() => {
          close();
          onNavigate?.();
        }}
      >
        <Receipt className="h-4 w-4" /> Billing &amp; plan
      </Link>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-bg-subtle"
        onClick={() => {
          openSupport('feature_idea', { mode: 'idea' });
          close();
          onNavigate?.();
        }}
      >
        <Lightbulb className="h-4 w-4" /> Suggest a feature
      </button>
      <div className="my-1 border-t border-border" />
      <button
        onClick={() => {
          logout();
          close();
        }}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger-subtle"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );

  if (variant === 'header') {
    return (
      <div className="relative hidden md:block" ref={menuRef}>
        {menu}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2.5 rounded-lg border-l border-border pl-3 pr-1 py-1 text-left transition-colors hover:bg-bg-subtle"
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <Avatar name={user?.user?.name ?? 'U'} size="sm" />
          <div className="leading-tight">
            <p className="max-w-[140px] truncate text-[13px] font-medium text-text-primary">
              {user?.workspace?.name ?? user?.user?.name}
            </p>
            <p className="max-w-[140px] truncate text-[11px] text-text-muted">{plan}</p>
          </div>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-text-muted transition-transform', open && 'rotate-180')}
          />
        </button>
      </div>
    );
  }

  return (
    <div className="relative border-t border-sidebar-border p-3" ref={menuRef}>
      {menu}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-sidebar-hover"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Avatar name={user?.user?.name ?? 'U'} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-white">{user?.user?.name ?? 'User'}</p>
          <p className="truncate text-[11px] text-sidebar-text">{plan}</p>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-sidebar-text transition-transform', open && 'rotate-180')}
        />
      </button>
    </div>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { usage } = useAuth();

  return (
    <div className="flex h-full flex-col bg-sidebar">
      <Link to="/dashboard" className="flex h-16 items-center gap-2.5 px-5">
        <BrandMark size="md" />
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-semibold text-white">{BRAND.name}</p>
          <p className="text-[11px] font-medium text-sidebar-text">Grow Renewals, Reduce Lapse.</p>
        </div>
      </Link>

      <nav className="scroll-slim flex-1 space-y-1 overflow-y-auto px-3 py-2">
        <p className="px-3 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-wider text-sidebar-text/60">
          Main
        </p>
        {navItems.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            label={label}
            icon={icon}
            active={pathname.startsWith(to)}
            onClick={onNavigate}
          />
        ))}

        <div className="px-3 pb-1.5 pt-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sidebar-text/60">Account</p>
        </div>
        {settingsItems.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            label={label}
            icon={icon}
            active={
              to === '/help'
                ? pathname === to
                : to === '/settings'
                  ? pathname === '/settings'
                  : pathname === to || pathname.startsWith(to + '/')
            }
            onClick={onNavigate}
          />
        ))}
      </nav>

      <DevPlanSwitcher />

      {usage?.trialDaysLeft != null && usage.trialDaysLeft > 0 && (
        <div className="mx-3 mb-3 rounded-xl bg-gradient-to-br from-brand/20 to-brand/5 p-3.5 ring-1 ring-white/10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-300" />
            <p className="text-[13px] font-semibold text-white">{usage.trialDaysLeft} days left on trial</p>
          </div>
          <p className="mt-1 text-[12px] leading-snug text-sidebar-text">
            Upgrade to keep your reminders sending.
          </p>
          <Link
            to="/settings/billing"
            onClick={onNavigate}
            className="mt-2.5 flex h-8 items-center justify-center rounded-lg bg-white text-[13px] font-semibold text-sidebar transition-colors hover:bg-blue-50"
          >
            Upgrade now
          </Link>
        </div>
      )}

      <UserMenuDropdown variant="sidebar" onNavigate={onNavigate} />
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { usage } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = search.trim();
    if (!query) return;
    navigate({
      to: '/agreements',
      search: { tab: 'all', q: query } as never,
    });
    setSearch('');
  };

  return (
    <div className="flex min-h-screen bg-bg-app">
      {/* Desktop sidebar */}
      <aside className="hidden w-[252px] shrink-0 md:block">
        <div className="fixed inset-y-0 left-0 w-[252px]">
          <SidebarContent />
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-[#0a0e17]/55 backdrop-blur-sm animate-fade-in" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[270px] animate-slide-in-right">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 z-10 rounded-lg p-1.5 text-sidebar-text hover:bg-sidebar-hover"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-bg-surface/85 px-4 backdrop-blur-md md:px-8">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2 text-text-secondary hover:bg-bg-subtle md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <form onSubmit={onSearch} className="relative hidden max-w-sm flex-1 sm:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agreements & customers…"
              className="h-9 w-full rounded-lg border border-border bg-bg-subtle/60 pl-9 pr-3 text-sm transition-colors placeholder:text-text-muted focus-visible:border-brand focus-visible:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
            />
          </form>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            {usage?.trialDaysLeft != null && usage.trialDaysLeft > 0 && (
              <Link
                to="/settings/billing"
                className="hidden items-center gap-1.5 rounded-full bg-warning-subtle px-3 py-1.5 text-xs font-semibold text-[#854D0E] ring-1 ring-[#FDE68A] transition-colors hover:bg-[#FEF3C7] sm:inline-flex"
              >
                {usage.trialDaysLeft} {usage.trialDaysLeft === 1 ? 'day' : 'days'} left · Upgrade
              </Link>
            )}
            <Link
              to="/reminders"
              className="relative rounded-lg p-2 text-text-secondary transition-colors hover:bg-bg-subtle hover:text-text-primary"
              aria-label="Reminder activity"
            >
              <Bell className="h-5 w-5" />
            </Link>
            <UserMenuDropdown variant="header" />
          </div>
        </header>

        <AppShellBanner />

        <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 md:px-8 md:py-8">
          <div className="space-y-3">
            <FeedbackPrompt />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
